export const SPREADSHEET_PREVIEW_MAX_SHEETS = 8
export const SPREADSHEET_PREVIEW_MAX_ROWS = 200
export const SPREADSHEET_PREVIEW_MAX_COLUMNS = 40

export interface SpreadsheetPreviewSheet {
  name: string
  rows: string[][]
  totalRows: number
  totalColumns: number
  truncated: boolean
}

export interface SpreadsheetPreviewDocument {
  kind: 'okclaw-spreadsheet-preview'
  version: 1
  sheets: SpreadsheetPreviewSheet[]
  truncated: boolean
  limits: {
    sheets: number
    rows: number
    columns: number
  }
}

export interface SpreadsheetRowWindow {
  start: number
  end: number
  topSpacerHeight: number
  bottomSpacerHeight: number
}

function toBoundedInteger(value: unknown, fallback: number, maximum: number): number {
  return typeof value === 'number' && Number.isFinite(value)
    ? Math.max(0, Math.min(maximum, Math.floor(value)))
    : fallback
}

/** Parse an untrusted preview payload without letting it fan out into excess DOM. */
export function parseSpreadsheetPreview(content: string): SpreadsheetPreviewDocument | null {
  let value: unknown
  try {
    value = JSON.parse(content)
  } catch {
    return null
  }

  if (!value || typeof value !== 'object') return null
  const raw = value as Record<string, unknown>
  if (raw.kind !== 'okclaw-spreadsheet-preview' || raw.version !== 1 || !Array.isArray(raw.sheets)) {
    return null
  }

  const sheets: SpreadsheetPreviewSheet[] = []
  for (const candidate of raw.sheets.slice(0, SPREADSHEET_PREVIEW_MAX_SHEETS)) {
    if (!candidate || typeof candidate !== 'object') continue
    const sheet = candidate as Record<string, unknown>
    if (!Array.isArray(sheet.rows)) continue

    const rows = sheet.rows
      .slice(0, SPREADSHEET_PREVIEW_MAX_ROWS)
      .filter(Array.isArray)
      .map(row => (row as unknown[])
        .slice(0, SPREADSHEET_PREVIEW_MAX_COLUMNS)
        .map(cell => typeof cell === 'string' ? cell : cell == null ? '' : String(cell)))

    if (rows.length === 0) continue
    const shownColumns = rows.reduce((maximum, row) => Math.max(maximum, row.length), 0)
    sheets.push({
      name: typeof sheet.name === 'string' && sheet.name.trim() ? sheet.name : `Sheet ${sheets.length + 1}`,
      rows,
      totalRows: toBoundedInteger(sheet.totalRows, rows.length, Number.MAX_SAFE_INTEGER),
      totalColumns: toBoundedInteger(sheet.totalColumns, shownColumns, Number.MAX_SAFE_INTEGER),
      truncated: sheet.truncated === true,
    })
  }

  if (sheets.length === 0) return null
  return {
    kind: 'okclaw-spreadsheet-preview',
    version: 1,
    sheets,
    truncated: raw.truncated === true,
    limits: {
      sheets: SPREADSHEET_PREVIEW_MAX_SHEETS,
      rows: SPREADSHEET_PREVIEW_MAX_ROWS,
      columns: SPREADSHEET_PREVIEW_MAX_COLUMNS,
    },
  }
}

export function getSpreadsheetColumnName(index: number): string {
  let value = Math.max(0, Math.floor(index)) + 1
  let label = ''
  while (value > 0) {
    value--
    label = String.fromCharCode(65 + (value % 26)) + label
    value = Math.floor(value / 26)
  }
  return label
}

/**
 * Calculate a bounded row window while reserving row zero for the frozen
 * worksheet header. Keeping this pure makes the virtualization geometry easy
 * to exercise with adversarial scroll positions.
 */
export function getSpreadsheetRowWindow(input: {
  rowCount: number
  scrollTop: number
  viewportHeight: number
  rowHeight: number
  columnHeaderHeight: number
  overscan?: number
}): SpreadsheetRowWindow {
  const rowCount = Math.max(0, Math.floor(input.rowCount))
  const rowHeight = Math.max(1, input.rowHeight)
  const scrollTop = Math.max(0, input.scrollTop)
  const viewportHeight = Math.max(0, input.viewportHeight)
  const overscan = Math.max(0, Math.floor(input.overscan ?? 6))
  if (rowCount <= 1) {
    return { start: 1, end: 0, topSpacerHeight: 0, bottomSpacerHeight: 0 }
  }

  const firstVisible = Math.floor((scrollTop - input.columnHeaderHeight) / rowHeight)
  const lastVisible = Math.ceil((scrollTop + viewportHeight - input.columnHeaderHeight) / rowHeight)
  const start = Math.max(1, Math.min(rowCount - 1, firstVisible - overscan))
  const end = Math.max(start, Math.min(rowCount - 1, lastVisible + overscan))
  return {
    start,
    end,
    topSpacerHeight: Math.max(0, start - 1) * rowHeight,
    bottomSpacerHeight: Math.max(0, rowCount - end - 1) * rowHeight,
  }
}

function visualCharacterCount(value: string): number {
  let count = 0
  for (const character of value.slice(0, 80)) {
    count += /[\u2E80-\u9FFF\uAC00-\uD7AF\uF900-\uFAFF]/.test(character) ? 2 : 1
  }
  return count
}

/** Estimate stable, readable widths without measuring every rendered cell. */
export function getSpreadsheetColumnWidths(rows: string[][]): number[] {
  const columnCount = rows.reduce((maximum, row) => Math.max(maximum, row.length), 0)
  return Array.from({ length: columnCount }, (_, columnIndex) => {
    let characters = 0
    for (const row of rows.slice(0, 80)) {
      characters = Math.max(characters, visualCharacterCount(row[columnIndex] ?? ''))
    }
    // A preview should expose the shape of the sheet before it exposes every
    // character. Compact bounds keep more business columns in view, while the
    // full value remains available through native text selection and tooltip.
    return Math.max(84, Math.min(240, Math.ceil(characters * 6.6 + 24)))
  })
}
